import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import EditIcon from '@mui/icons-material/Edit'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import NewspaperIcon from '@mui/icons-material/Newspaper'
import HandshakeIcon from '@mui/icons-material/Handshake'
import GroupsIcon from '@mui/icons-material/Groups'
import KingBedIcon from '@mui/icons-material/KingBed'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import LocalActivityIcon from '@mui/icons-material/LocalActivity'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import { useTranslation } from 'react-i18next'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import IconButtonOutlined from '../root/components/IconButtonOutlined'
import { getOption, useEventsTypeOptions } from '../../utils'
import { Card } from '../dashboard/Dashboard'
import EventStatusTag from '../events/components/EventStatusTag'
import EventInfoBox from '../events/components/EventInfoBox'
import { useLazyGetQRCodeQuery } from '../../services/events'
import { LoaderData } from '../../types'
import { adminEventsLoader } from './Admin.loader'

export default function Admin() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const eventsTypeOptions = useEventsTypeOptions(t)
  const data = useLoaderData() as LoaderData<ReturnType<typeof adminEventsLoader>>
  const [getQRCode] = useLazyGetQRCodeQuery()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const ResponsiveButton = isMobile ? IconButtonOutlined : Button

  return data?.map(event => {
    const type = getOption(eventsTypeOptions, undefined, event.type)
    return (
      <Card
        key={event.id}
        color={type?.color}
        onClick={() => {
          navigate(`/admin/details/${event.id}/participants`)
        }}
        sx={{
          minWidth: '100%',
          minHeight: '15rem',
          height: 'content',
          padding: 0,
          gap: '0',
        }}
      >
        <Stack
          gap="1rem"
          padding="0.5rem 0.5rem 0.25rem"
          direction="row"
          sx={theme => ({ backgroundColor: theme.palette.divider })}
          justifyContent="space-between"
          width="-webkit-fill-available"
        >
          <Stack>
            <EventStatusTag status={event.status} />
          </Stack>
          <Stack direction="row" gap="0.5rem">
            <IconButtonOutlined
              size="small"
              onClick={e => {
                e.stopPropagation()
                navigate(`/events/${event.id}`)
              }}
              title={t('Voir le programme')}
            >
              <VisibilityIcon fontSize="small" />
            </IconButtonOutlined>
            <IconButtonOutlined
              size="small"
              onClick={e => {
                e.stopPropagation()
                navigate(`/admin/events/${event.id}/event`)
              }}
              title={t('Modifier l’événement')}
            >
              <EditIcon fontSize="small" />
            </IconButtonOutlined>
            <IconButtonOutlined
              size="small"
              onClick={e => {
                e.stopPropagation()
                void getQRCode(event.id)
              }}
              title={t('Générer le QR code')}
            >
              <QrCode2Icon fontSize="small" />
            </IconButtonOutlined>
          </Stack>
        </Stack>
        <Stack padding="0.5rem 1rem 1rem" width="-webkit-fill-available">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <h4>{event.name}</h4>
            <Chip
              color="primary"
              sx={{
                backgroundColor: type?.color,
              }}
              label={type?.label}
            />
          </Stack>
          <Stack direction="column">
            <EventInfoBox
              start={event.start}
              end={event.end}
              location={event.location}
              direction="row"
            />
            <Divider sx={{ margin: '1rem 0' }} />
            <Stack direction="row" gap="0.5rem" flexWrap="wrap">
              <Stack direction="row" gap="0.5rem" flex="1 1 auto">
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<ConfirmationNumberIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/options`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <ConfirmationNumberIcon /> : t('Options')}
                </ResponsiveButton>
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<GroupsIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/participants`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <GroupsIcon /> : t('Participants')}
                </ResponsiveButton>
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<LocalActivityIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/contributors`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <LocalActivityIcon /> : t('Contributeurs')}
                </ResponsiveButton>
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<NewspaperIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/abstracts`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <NewspaperIcon /> : t('Résumés')}
                </ResponsiveButton>
              </Stack>
              <Stack direction="row" gap="0.5rem" flex="1 1 auto">
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<HandshakeIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/sponsorship`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <HandshakeIcon /> : t('P. charge')}
                </ResponsiveButton>
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<KingBedIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/hotels`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <KingBedIcon /> : t('Hôtels')}
                </ResponsiveButton>
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<RestaurantIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/meals`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <RestaurantIcon /> : t('Repas')}
                </ResponsiveButton>
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  startIcon={<CalendarMonthIcon />}
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/details/${event.id}/subevents`)
                  }}
                  sx={{ flex: '1 0 0' }}
                >
                  {isMobile ? <CalendarMonthIcon /> : t('Interventions')}
                </ResponsiveButton>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="-webkit-fill-available"
          />
        </Stack>
      </Card>
    )
  })
}
